import { createSelector } from '@reduxjs/toolkit'
import type { FetchBaseQueryError, FetchBaseQueryMeta, QueryReturnValue } from '@reduxjs/toolkit/query'
import { createApi } from '@reduxjs/toolkit/query/react'
import { dedupe, logger } from '@tunasong/models'
import type { PerformancePart, Persisted, Profile } from '@tunasong/schemas'
import type { Meta } from './base-query.js'
import baseQuery, { createCachedQuerySpecHelper } from './base-query.js'

// Define a service using a base URL and expected endpoints
/**
 * This type is self referencing - https://github.com/arktypeio/arktype/discussions/740#discussioncomment-5777358
 */

const apiName = 'profiles-api'
const cachedQuerySpec = createCachedQuerySpecHelper(apiName)

/**
 * The signed-in user's own profile holds their config, which the app PATCHes as a whole, so it must be
 * read from the network. The service worker serves `api/profiles/*` stale-while-revalidate, and cannot
 * repair a stale copy: profile responses carry no ETag, so its broadcast compares Content-Length only,
 * and a small config change often gzips to the same length. A stale config is then kept for the visit
 * and written back over the server on the next save. `Cache-Control: no-cache` makes the SW go to the
 * network and still fall back to its cache when offline (`forceNoCachePlugin`).
 * Other users' profiles (avatars, names) keep the cached path.
 */
const profilesBaseQuery: typeof baseQuery = (args, api, extraOptions) => {
  const userId = (api.getState() as { user?: { userId?: string } }).user?.userId
  const isOwnProfileLoad =
    api.endpoint === 'loadProfile' &&
    typeof args !== 'string' &&
    Boolean(userId) &&
    args.url === `profiles/${encodeURIComponent(userId ?? '')}`
  if (!isOwnProfileLoad) {
    return baseQuery(args, api, extraOptions)
  }
  return baseQuery(
    { ...args, headers: { ...(args.headers as Record<string, string>), 'Cache-Control': 'no-cache' } },
    api,
    extraOptions
  )
}

const loadProfileSpec = cachedQuerySpec('loadProfile', ({ emailOrUserId }: { emailOrUserId: string }) => ({
  url: `profiles/${encodeURIComponent(emailOrUserId)}`,
}))

/**
 * Profiles requested in the same tick load in one batched request (GH #2846): every avatar, hover card and
 * collaborator row on a page asks for its own profile, which made one request per person. The batch endpoint returns
 * the same per-user view as the single one (`callerView` on the stored profile).
 *
 * Kept on the single request: the signed-in user's own profile (read past the service worker, see
 * `profilesBaseQuery`), a lookup by email, a profile asked for alone, and any id a batch does not return or a failed
 * batch held, since the single endpoint also creates a profile it does not have yet from Cognito.
 */
const maxBatchIds = 50

const isBatchable = (emailOrUserId: string, ownUserId: string | undefined) =>
  Boolean(emailOrUserId) && emailOrUserId !== ownUserId && !emailOrUserId.includes('@')

type FetchBatch = (ids: string[]) => Promise<Persisted<Profile>[] | undefined>
interface BatchWaiter {
  id: string
  resolve: (profile: Persisted<Profile> | undefined) => void
}
let batchWaiters: BatchWaiter[] = []

const runBatch = async (waiters: BatchWaiter[], fetchBatch: FetchBatch) => {
  const ids = dedupe(waiters.map(waiter => waiter.id))
  /** A profile asked for alone is the single request it was */
  if (ids.length < 2) {
    waiters.forEach(waiter => waiter.resolve(undefined))
    return
  }
  const profiles = await fetchBatch(ids).catch((error: unknown) => {
    logger.warn('Batched profile load failed; loading those profiles one by one', error)
    return undefined
  })
  const byId = new Map((profiles ?? []).map(profile => [profile.userId, profile]))
  waiters.forEach(waiter => waiter.resolve(byId.get(waiter.id)))
}

/** The profile from this tick's batch, or `undefined` for the caller to load it on its own */
const batchedProfile = (id: string, fetchBatch: FetchBatch) =>
  new Promise<Persisted<Profile> | undefined>(resolve => {
    if (batchWaiters.length === 0) {
      setTimeout(() => {
        const waiters = batchWaiters
        batchWaiters = []
        const chunks = Array.from({ length: Math.ceil(waiters.length / maxBatchIds) }, (_, i) =>
          waiters.slice(i * maxBatchIds, (i + 1) * maxBatchIds)
        )
        chunks.forEach(chunk => void runBatch(chunk, fetchBatch))
      }, 0)
    }
    batchWaiters.push({ id, resolve })
  })

export const profilesApi = createApi({
  reducerPath: apiName,
  tagTypes: ['Profile', 'BandParts'],
  /** Keep profiles for an hour */
  keepUnusedDataFor: 3600,

  baseQuery: profilesBaseQuery,
  endpoints: builder => ({
    /** @todo we get instantiation depth problems using Profile here */
    loadProfiles: builder.query<Persisted<Profile>[], { userIds: string[] }>({
      keepUnusedDataFor: 3600,
      providesTags: result => result?.map(r => ({ type: 'Profile' as const, id: r.userId })) ?? [],

      query: cachedQuerySpec('loadProfiles', ({ userIds }) => ({
        url: `profiles/?ids=${encodeURIComponent(dedupe(userIds).join(','))}`,
      })),
      async onQueryStarted({}, { dispatch, queryFulfilled }) {
        /**
         * RTK calls `onQueryStarted` without attaching a catch, so an unguarded
         * `await queryFulfilled` turns any failed request — a 403 on a profile
         * the user cannot read is routine — into an unhandled rejection carrying
         * a plain `{ error, isUnhandledError, meta }` object, which Sentry
         * records stackless and unactionable (GH #1731).
         */
        try {
          const result = await queryFulfilled
          for (const profile of result.data) {
            /** to avoid infinite type recursion we cast profile to never */
            void dispatch(
              profilesApi.util.upsertQueryData('loadProfile', { emailOrUserId: 'PUBLIC' }, profile as never)
            )
          }
        } catch (e) {
          logger.warn('Error loading profiles', e)
        }
      },
    }),
    loadProfile: builder.query<Persisted<Profile>, { emailOrUserId: string }>({
      providesTags: result => [{ type: 'Profile' as const, id: result?.userId }],
      keepUnusedDataFor: 3600,
      queryFn: async ({ emailOrUserId }, api, _extraOptions, query) => {
        const single = async () =>
          (await query(loadProfileSpec({ emailOrUserId }))) as QueryReturnValue<
            Persisted<Profile>,
            FetchBaseQueryError,
            Meta & FetchBaseQueryMeta
          >
        const ownUserId = (api.getState() as { user?: { userId?: string } }).user?.userId
        if (!isBatchable(emailOrUserId, ownUserId)) {
          return single()
        }
        const batched = await batchedProfile(emailOrUserId, async ids => {
          const result = await query({ url: `profiles/?ids=${encodeURIComponent(ids.join(','))}` })
          return Array.isArray(result.data) ? (result.data as Persisted<Profile>[]) : undefined
        })
        return batched ? { data: batched } : single()
      },
    }),
    /**
     * The emails of an entity's members (userId → email), for its owner and the people it is shared with by name.
     * Public profiles carry no email. `memberKey` is a cache key only, never sent: a changed member list refetches.
     */
    loadMemberEmails: builder.query<
      { entityId: string; emails: Record<string, string> },
      { entityId: string; memberKey: string }
    >({
      keepUnusedDataFor: 300,
      query: cachedQuerySpec('loadMemberEmails', ({ entityId }) => ({
        url: `profiles/emails?entity=${encodeURIComponent(entityId)}`,
      })),
    }),
    /**
     * The parts each member plays in one band (userId → parts), for bandmates' profile cards, and the members who do
     * not perform with it. `notPerforming` is optional: a backend that predates it omits the field.
     */
    loadBandParts: builder.query<
      { bandId: string; parts: Record<string, PerformancePart[]>; notPerforming?: string[] },
      { bandId: string }
    >({
      providesTags: ['BandParts'],
      keepUnusedDataFor: 300,
      query: cachedQuerySpec('loadBandParts', ({ bandId }) => ({
        url: `profiles/band-parts?band=${encodeURIComponent(bandId)}`,
      })),
    }),
    updateProfile: builder.mutation<Persisted<Profile>, { id: string; profile: Partial<Profile> }>({
      /** A changed part set must show on your own card; profile tags are kept, see onQueryStarted */
      invalidatesTags: ['BandParts'],
      /** @note we update the Redux cache on query, so we don't need to invalidate these tags here */
      // invalidatesTags: result => [{ type: 'Profile' as const, id: result?.userId }],
      query: ({ id, profile }) => ({
        url: `profiles/${id}`,
        method: 'PATCH',
        body: profile,
      }),
      /** Immediately update the cache on update */
      async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          profilesApi.util.updateQueryData('loadProfile', { emailOrUserId: id }, draft => {
            // RTK passes the raw cached value when it is not draftable, so a
            // profile that resolved to `null` arrives here as `null` and
            // `Object.assign` throws. GH #1730
            if (!draft) {
              return
            }
            Object.assign(draft, patch.profile)
          })
        )
        try {
          await queryFulfilled
        } catch {
          patchResult.undo()
        }
      },
    }),
  }),
})

interface MemberEmails {
  entityId: string
  emails: Record<string, string>
}

/**
 * Every member email this client has been given (userId → email), across the entities it loaded them for. Profile
 * hooks fill a missing `email` from it, so people without a name still read as their email to their fellow members.
 */
export const selectMemberEmails = createSelector(
  [(state: { [apiName]: ReturnType<typeof profilesApi.reducer> }) => state[apiName].queries],
  queries => {
    const emails: Record<string, string> = {}
    for (const query of Object.values(queries)) {
      if (query?.endpointName === 'loadMemberEmails' && query.data) {
        Object.assign(emails, (query.data as MemberEmails).emails)
      }
    }
    return emails
  }
)
